import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import './DomainInventory.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus =
  | 'queued'
  | 'phase_a_running'
  | 'phase_a_done'
  | 'awaiting_confirmation'
  | 'cancelled'
  | 'phase_b1_running'
  | 'phase_b2_waiting'
  | 'phase_b3_running'
  | 'completed'
  | 'failed';

type ItemStatus =
  | 'pending'
  | 'not_found'
  | 'b1_user_deleted'
  | 'b3_verified_deleted'
  | 'error';

interface RemovalRun {
  id: string;
  status: RunStatus;
  triggered_by: string | null;
  raw_input: string;
  emails_pasted: number;
  inventory_run_id: string | null;
  emails_requested: number;
  emails_targeted: number;
  emails_not_found: number;
  users_deleted: number;
  users_verified: number;
  users_failed: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  confirmed_at: string | null;
  finished_at: string | null;
}

interface RemovalRunItem {
  id: string;
  run_id: string;
  user_email: string;
  domain_name: string;
  inventory_panel_id: string | null;
  panel_email: string | null;
  estate: 'google' | 'microsoft';
  ms_tenant_id: number | null;
  panel_reseller_id: string | null;
  status: ItemStatus;
  b1_attempts: number;
  b3_attempts: number;
  error_code: string | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface RunDetail {
  run: RemovalRun;
  items: RemovalRunItem[];
}

interface WillDeleteEntry {
  user_email: string;
  domain_name: string;
  panel_email?: string | null;
  panel_reseller_id?: string | null;
  estate?: 'google' | 'microsoft';
  ms_tenant_id?: number | null;
}

interface NotFoundEntry {
  user_email: string;
  domain_name: string;
}

interface PreviewResponse {
  run: RemovalRun;
  willDelete: WillDeleteEntry[];
  notFound: NotFoundEntry[];
}

interface InventoryRunSummary {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  domains_total: number;
  started_at: string | null;
  finished_at: string | null;
}

interface InventoryRunDetailResponse {
  run: InventoryRunSummary;
  items: unknown[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Constants ──────────────────────────────────────────── */

const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

const INFLIGHT_STATUSES: ReadonlySet<RunStatus> = new Set([
  'queued',
  'phase_a_running',
  'phase_a_done',
  'awaiting_confirmation',
  'phase_b1_running',
  'phase_b2_waiting',
  'phase_b3_running',
]);

/* Mirrors cancelRun() in the user-removal-runs repo: nothing is deleted yet,
   so the run can still be resumed into the wizard or cancelled. */
const PRE_CONFIRM_STATUSES: ReadonlySet<RunStatus> = new Set([
  'queued',
  'phase_a_running',
  'phase_a_done',
  'awaiting_confirmation',
]);

const INITIAL_VISIBLE = 15;
const PAGE_INCREMENT = 40;

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '-';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

function statusBadgeClass(status: RunStatus): string {
  switch (status) {
    case 'queued':
      return 'cln-badge--queued';
    case 'phase_a_running':
    case 'phase_b1_running':
    case 'phase_b3_running':
      return 'cln-badge--running';
    case 'phase_a_done':
    case 'awaiting_confirmation':
      return 'cln-badge--honey';
    case 'phase_b2_waiting':
      return 'cln-badge--queued';
    case 'completed':
      return 'cln-badge--completed';
    case 'failed':
      return 'cln-badge--failed';
    case 'cancelled':
      return 'cln-badge--muted';
    default:
      return 'cln-badge--muted';
  }
}

function itemBadgeClass(status: ItemStatus): string {
  switch (status) {
    case 'pending':
      return 'cln-badge--pending';
    case 'not_found':
      return 'cln-badge--muted';
    case 'b1_user_deleted':
      return 'cln-badge--running';
    case 'b3_verified_deleted':
      return 'cln-badge--success';
    case 'error':
      return 'cln-badge--error';
    default:
      return 'cln-badge--muted';
  }
}

const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const DownloadIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ─── View modes ─────────────────────────────────────────── */

type View =
  | { mode: 'list' }
  | { mode: 'wizard'; resumeRunId?: string }
  | { mode: 'detail'; runId: string };

/* ─── Main component ─────────────────────────────────────── */

export function UserRemoval(): JSX.Element {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [runs, setRuns] = useState<RemovalRun[] | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RemovalRun[]>('/api/cleanup/user-removal/runs');
      setRuns((prev) => {
        if (!prev) return data;
        const headIds = new Set(data.map((r) => r.id));
        const older = prev.filter((r) => !headIds.has(r.id));
        return [...data, ...older];
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast]);

  useEffect(() => {
    if (view.mode === 'list') void refreshList();
  }, [view.mode, refreshList]);

  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (view.mode !== 'list') {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
      return;
    }
    const hasInflight = runs?.some((r) => INFLIGHT_STATUSES.has(r.status));
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [view.mode, runs, refreshList]);

  if (view.mode === 'detail') {
    return (
      <RunDetailView
        runId={view.runId}
        onBack={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onResume={(runId) => setView({ mode: 'wizard', resumeRunId: runId })}
        onError={(msg) => showToast('error', msg)}
        toast={toast}
      />
    );
  }

  if (view.mode === 'wizard') {
    return (
      <NewRunWizard
        resumeRunId={view.resumeRunId}
        onCancelToList={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onOpenDetail={(runId) => setView({ mode: 'detail', runId })}
        showToast={showToast}
        toast={toast}
      />
    );
  }

  return (
    <RunsListView
      runs={runs}
      onNew={() => setView({ mode: 'wizard' })}
      onOpen={(runId) => setView({ mode: 'detail', runId })}
      onRefresh={() => void refreshList()}
      toast={toast}
    />
  );
}

/* ─── State 1: List view ─────────────────────────────────── */

function RunsListView({
  runs,
  onNew,
  onOpen,
  onRefresh,
  toast,
}: {
  runs: RemovalRun[] | null;
  onNew: () => void;
  onOpen: (runId: string) => void;
  onRefresh: () => void;
  toast: Toast | null;
}): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const totalRuns = runs?.length ?? 0;
  const visibleRuns = runs ? runs.slice(0, visibleCount) : null;
  const inflight = runs?.find((r) => INFLIGHT_STATUSES.has(r.status)) ?? null;

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <div className="cln-header-row">
            <div>
              <h1 className="cln-title">User Removal</h1>
              <p className="cln-subtitle">
                Delete specific user accounts across the inventory pool by pasting a list of email addresses.
              </p>
            </div>
            <button
              type="button"
              className="cln-btn cln-btn--primary"
              onClick={onNew}
              disabled={inflight !== null}
              title={inflight ? 'A removal run is already in progress' : 'Start a new user removal run'}
            >
              <PlusIcon />
              {inflight ? 'Run in progress...' : 'New User Removal'}
            </button>
          </div>
        </header>

        {inflight && (
          <section className="cln-card cln-snapshot-card">
            <div className="cln-snapshot-meta">
              An active user-removal run is in flight ({inflight.status}). Click the row below to view its
              progress.
            </div>
          </section>
        )}

        <section className="cln-card">
          <div className="cln-card-header">
            <h2 className="cln-card-title">
              Runs <span className="cln-cell-faint">({Math.min(visibleCount, totalRuns)} of {totalRuns})</span>
            </h2>
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh list">
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="cln-empty">Loading...</div>}

          {runs !== null && runs.length === 0 && (
            <div className="cln-empty">
              <h3 className="cln-empty-title">No runs yet</h3>
              <p className="cln-empty-text">
                Click <strong>New User Removal</strong> above to paste a list of emails to delete.
              </p>
            </div>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && (
            <table className="cln-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th className="cln-col-num">Emails</th>
                  <th className="cln-col-num">Users deleted</th>
                  <th className="cln-col-num">Failed</th>
                  <th>By</th>
                  <th className="cln-col-date">Started</th>
                  <th className="cln-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((r) => (
                  <tr key={r.id} className="cln-row" onClick={() => onOpen(r.id)}>
                    <td className="cln-cell-mono cln-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`cln-badge ${statusBadgeClass(r.status)}`}>{r.status}</span>
                    </td>
                    <td className="cln-col-num cln-num--ink">{r.emails_targeted}</td>
                    <td className="cln-col-num">{r.users_deleted}</td>
                    <td className="cln-col-num">{r.users_failed}</td>
                    <td className="cln-cell-mono cln-cell-faint">{r.triggered_by ?? '-'}</td>
                    <td className="cln-col-date cln-cell-faint">
                      {relativeTime(r.started_at ?? r.created_at)}
                    </td>
                    <td className="cln-col-date cln-cell-faint">
                      {durationLabel(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {visibleRuns !== null && totalRuns > visibleCount && (
            <div className="cln-loadmore-row">
              <button
                type="button"
                className="cln-btn cln-btn--ghost"
                onClick={() => setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, totalRuns))}
              >
                Load more
              </button>
            </div>
          )}
        </section>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── State 2: New-run wizard ────────────────────────────── */

type WizardStep = 'emails' | 'phase_a' | 'preview';

function NewRunWizard({
  resumeRunId,
  onCancelToList,
  onOpenDetail,
  showToast,
  toast,
}: {
  resumeRunId?: string;
  onCancelToList: () => void;
  onOpenDetail: (runId: string) => void;
  showToast: (type: Toast['type'], message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  /* Resuming (e.g. after a page refresh) re-enters the Phase A poll, which
     advances to the preview step once the run is awaiting_confirmation. */
  const [step, setStep] = useState<WizardStep>(resumeRunId ? 'phase_a' : 'emails');
  const [emailsText, setEmailsText] = useState('');
  const [starting, setStarting] = useState(false);
  const [runId, setRunId] = useState<string | null>(resumeRunId ?? null);
  const [run, setRun] = useState<RemovalRun | null>(null);
  const [inventoryRun, setInventoryRun] = useState<InventoryRunSummary | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  /* Naive client-side counter, just for the input panel. The server's parser
     is the source of truth for what gets sent to delete. */
  const pastedCount = emailsText
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0 && /@/.test(t)).length;

  const handleStart = async (): Promise<void> => {
    const text = emailsText.trim();
    if (text.length === 0) {
      showToast('error', 'Paste at least one email address first.');
      return;
    }
    setStarting(true);
    try {
      const result = await api<{ runId: string; emailsPasted: number; invalidTokens: number }>(
        '/api/cleanup/user-removal/runs',
        { method: 'POST', body: JSON.stringify({ emails: text }) },
      );
      setRunId(result.runId);
      if (result.invalidTokens > 0) {
        showToast(
          'success',
          `Started with ${result.emailsPasted} valid emails (${result.invalidTokens} invalid tokens dropped).`,
        );
      }
      setStep('phase_a');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A removal run is already in progress - opening it now.');
          onOpenDetail(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to start run';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* Phase A polling: 1s. */
  const phaseAPollRef = useRef<number | null>(null);
  useEffect(() => {
    if (step !== 'phase_a' || !runId) {
      if (phaseAPollRef.current !== null) {
        window.clearInterval(phaseAPollRef.current);
        phaseAPollRef.current = null;
      }
      return;
    }

    let stopped = false;

    const poll = async (): Promise<void> => {
      try {
        const detail = await api<RunDetail>(`/api/cleanup/user-removal/runs/${runId}`);
        if (stopped) return;
        setRun(detail.run);

        if (detail.run.inventory_run_id) {
          try {
            const invDetail = await api<InventoryRunDetailResponse>(
              `/api/cleanup/inventory/runs/${detail.run.inventory_run_id}`,
            );
            if (!stopped) setInventoryRun(invDetail.run);
          } catch {
            // Inventory detail might 404 momentarily during creation race; ignore.
          }
        }

        if (detail.run.status === 'awaiting_confirmation') {
          try {
            const prev = await api<PreviewResponse>(
              `/api/cleanup/user-removal/runs/${runId}/preview`,
            );
            if (!stopped) {
              setPreview(prev);
              setStep('preview');
            }
          } catch (err) {
            if (!stopped) {
              showToast('error', err instanceof Error ? err.message : 'Failed to load preview');
            }
          }
          return;
        }

        if (
          detail.run.status === 'failed' ||
          detail.run.status === 'cancelled' ||
          (detail.run.status !== 'queued' &&
            detail.run.status !== 'phase_a_running' &&
            detail.run.status !== 'phase_a_done')
        ) {
          if (!stopped) onOpenDetail(runId);
        }
      } catch (err) {
        if (!stopped) {
          showToast('error', err instanceof Error ? err.message : 'Failed to poll run');
        }
      }
    };

    void poll();
    phaseAPollRef.current = window.setInterval(() => {
      void poll();
    }, 1000);

    return () => {
      stopped = true;
      if (phaseAPollRef.current !== null) {
        window.clearInterval(phaseAPollRef.current);
        phaseAPollRef.current = null;
      }
    };
  }, [step, runId, showToast, onOpenDetail]);

  const handleConfirm = async (): Promise<void> => {
    if (!runId || typedConfirmation !== 'DELETE') return;
    setConfirming(true);
    try {
      await api<{ runId: string; shards: number; items: number }>(
        `/api/cleanup/user-removal/runs/${runId}/confirm`,
        { method: 'POST', body: JSON.stringify({ typedConfirmation: 'DELETE' }) },
      );
      onOpenDetail(runId);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to confirm run';
      showToast('error', msg);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!runId) {
      onCancelToList();
      return;
    }
    setCancelling(true);
    try {
      await api<{ runId: string; status: 'cancelled' }>(
        `/api/cleanup/user-removal/runs/${runId}/cancel`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      onCancelToList();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to cancel run';
      showToast('error', msg);
    } finally {
      setCancelling(false);
    }
  };

  /* Phase A progress numbers */
  const phaseAPanelsTotal = inventoryRun?.panels_total ?? 0;
  const phaseAPanelsDone = inventoryRun?.panels_done ?? 0;
  const phaseADomainsTotal = inventoryRun?.domains_total ?? 0;
  const phaseAPct =
    phaseAPanelsTotal > 0
      ? Math.min(100, Math.round((phaseAPanelsDone / phaseAPanelsTotal) * 100))
      : 0;
  const phaseAElapsed = inventoryRun ? durationLabel(inventoryRun.started_at, inventoryRun.finished_at) : '-';

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <button
            type="button"
            className="cln-back"
            onClick={() => {
              if (runId) {
                void handleCancel();
              } else {
                onCancelToList();
              }
            }}
          >
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
          <div style={{ marginTop: 16 }}>
            <h1 className="cln-title">New User Removal</h1>
            <p className="cln-subtitle">
              Paste a list of user email addresses to delete. Phase A pulls a fresh inventory snapshot so each
              email can be matched to its owning admin panel, then you confirm the targeted users before deletion.
            </p>
          </div>
        </header>

        <div className="cln-wizard-container">
          {/* Step 1: emails textarea */}
          <section className="cln-wizard-step">
            <div className="cln-wizard-step-header">Step 1 - Email addresses to delete</div>
            <textarea
              className="cln-search-input"
              placeholder={'user1@example.com\nuser2@example.com\n...'}
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              disabled={step !== 'emails' || starting}
              rows={10}
              style={{
                width: '100%',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                resize: 'vertical',
                minHeight: 200,
                padding: 12,
                marginTop: 8,
              }}
            />
            <div className="cln-wizard-row" style={{ marginTop: 12, alignItems: 'center' }}>
              <div className="cln-cell-faint" style={{ flex: 1, fontSize: 13 }}>
                Whitespace, commas, or semicolons accepted. Duplicates are dropped automatically.
                {pastedCount > 0 && (
                  <> Pasted: <strong>{pastedCount}</strong></>
                )}
              </div>
              <button
                type="button"
                className="cln-btn cln-btn--primary"
                onClick={handleStart}
                disabled={step !== 'emails' || starting || emailsText.trim().length === 0}
              >
                {starting ? 'Starting...' : 'Start'}
              </button>
            </div>
          </section>

          {/* Step 2: Phase A progress */}
          {(step === 'phase_a' || step === 'preview') && (
            <section className="cln-wizard-step">
              <div className="cln-wizard-step-header">Step 2 - Domain inventory pull (Phase A)</div>
              <div className="cln-stage-row" style={{ gridTemplateColumns: '1fr auto auto auto' }}>
                <div className="cln-stage-progress">
                  <div className="cln-stage-progress-bar" style={{ width: `${phaseAPct}%` }} />
                </div>
                <div className="cln-stage-elapsed">
                  {phaseAPanelsDone}/{phaseAPanelsTotal} panels
                </div>
                <div className="cln-stage-elapsed">
                  {phaseADomainsTotal} domains
                </div>
                <div className="cln-stage-elapsed">{phaseAElapsed}</div>
              </div>
              {step === 'preview' && (
                <div className="cln-stage-name" style={{ marginTop: 8 }}>
                  Phase A complete - ready to preview deletion targets.
                </div>
              )}
              {step === 'phase_a' && run && (
                <div className="cln-stage-name" style={{ marginTop: 8 }}>
                  Run {run.id.slice(0, 8)} - status: {run.status}
                </div>
              )}
            </section>
          )}

          {/* Step 3: Preview + typed confirmation */}
          {step === 'preview' && preview && (
            <section className="cln-wizard-step">
              <div className="cln-wizard-step-header">Step 3 - Preview &amp; confirm</div>

              <WillDeletePreview entries={preview.willDelete} />
              <NotFoundPreview entries={preview.notFound} />

              <div className="cln-wizard-row" style={{ marginTop: 24, alignItems: 'center' }}>
                <input
                  type="text"
                  className="cln-confirm-input"
                  placeholder="Type DELETE"
                  value={typedConfirmation}
                  onChange={(e) => setTypedConfirmation(e.target.value)}
                  disabled={confirming || cancelling}
                />
                <button
                  type="button"
                  className="cln-btn cln-btn--primary"
                  onClick={handleConfirm}
                  disabled={
                    typedConfirmation !== 'DELETE' ||
                    confirming ||
                    cancelling ||
                    preview.willDelete.length === 0
                  }
                  title={
                    preview.willDelete.length === 0
                      ? 'Nothing to delete - cancel and try a different list'
                      : 'Delete the listed users'
                  }
                >
                  {confirming ? 'Confirming...' : 'Delete Users'}
                </button>
                <button
                  type="button"
                  className="cln-btn cln-btn--ghost"
                  onClick={handleCancel}
                  disabled={confirming || cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function WillDeletePreview({ entries }: { entries: WillDeleteEntry[] }): JSX.Element {
  const google = entries.filter((e) => e.estate !== 'microsoft');
  const microsoft = entries.filter((e) => e.estate === 'microsoft');
  return (
    <div className="cln-preview-list">
      <div className="cln-preview-list-title cln-preview-list-title--rose">
        Will delete ({entries.length}) - Google {google.length} / Microsoft {microsoft.length}
      </div>
      {entries.length === 0 ? (
        <div className="cln-cell-faint" style={{ fontSize: 13 }}>(none)</div>
      ) : (
        <>
          {google.length > 0 && (
            <table className="cln-table">
              <tbody>
                {google.map((e, i) => (
                  <tr key={`g-${e.user_email}-${i}`}>
                    <td className="cln-cell-mono">{e.user_email}</td>
                    <td className="cln-cell-mono cln-cell-faint">
                      {e.panel_reseller_id ?? '-'}
                      {e.panel_email ? ` (${e.panel_email})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {microsoft.length > 0 && (
            <>
              <div className="cln-preview-list-title" style={{ marginTop: 12 }}>
                Microsoft ({microsoft.length})
              </div>
              <table className="cln-table">
                <tbody>
                  {microsoft.map((e, i) => (
                    <tr key={`m-${e.user_email}-${i}`}>
                      <td className="cln-cell-mono">{e.user_email}</td>
                      <td className="cln-cell-mono cln-cell-faint">
                        Microsoft tenant #{e.ms_tenant_id ?? '?'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}

function NotFoundPreview({ entries }: { entries: NotFoundEntry[] }): JSX.Element {
  return (
    <div className="cln-preview-list">
      <div className="cln-preview-list-title cln-preview-list-title--muted">
        Not found in inventory ({entries.length})
      </div>
      {entries.length === 0 ? (
        <div className="cln-cell-faint" style={{ fontSize: 13 }}>(none)</div>
      ) : (
        <table className="cln-table">
          <tbody>
            {entries.map((e, i) => (
              <tr key={`${e.user_email}-${i}`}>
                <td className="cln-cell-mono">{e.user_email}</td>
                <td className="cln-cell-mono cln-cell-faint">{e.domain_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── State 3: Run detail ────────────────────────────────── */

function RunDetailView({
  runId,
  onBack,
  onResume,
  onError,
  toast,
}: {
  runId: string;
  onBack: () => void;
  onResume: (runId: string) => void;
  onError: (msg: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const refreshDetail = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RunDetail>(`/api/cleanup/user-removal/runs/${runId}`);
      setDetail(data);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [runId, onError]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  const detailPollRef = useRef<number | null>(null);
  useEffect(() => {
    detailPollRef.current = window.setInterval(() => {
      void refreshDetail();
    }, 2500);
    return () => {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [refreshDetail]);

  useEffect(() => {
    if (!detail) return;
    if (TERMINAL_STATUSES.has(detail.run.status)) {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    }
  }, [detail]);

  const handleCancel = async (): Promise<void> => {
    setCancelling(true);
    try {
      await api<{ runId: string; status: 'cancelled' }>(
        `/api/cleanup/user-removal/runs/${runId}/cancel`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      await refreshDetail();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to cancel run');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <button type="button" className="cln-back" onClick={onBack}>
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
        </header>
        {detail ? (
          <RunDetailBody
            detail={detail}
            onRefresh={() => void refreshDetail()}
            onResume={() => onResume(runId)}
            onCancel={() => void handleCancel()}
            cancelling={cancelling}
          />
        ) : (
          <div className="cln-loading">Loading run...</div>
        )}
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function RunDetailBody({
  detail,
  onRefresh,
  onResume,
  onCancel,
  cancelling,
}: {
  detail: RunDetail;
  onRefresh: () => void;
  onResume: () => void;
  onCancel: () => void;
  cancelling: boolean;
}): JSX.Element {
  const { run, items } = detail;

  const sortRank = (s: ItemStatus): number => {
    if (s === 'error') return 0;
    if (s === 'pending' || s === 'b1_user_deleted') return 1;
    return 2;
  };
  const sortedItems = [...items].sort((a, b) => sortRank(a.status) - sortRank(b.status));

  return (
    <>
      <section className="cln-summary-card">
        <div className="cln-summary-top">
          <div>
            <h2 className="cln-summary-title">
              Run <span className="cln-cell-mono cln-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="cln-summary-meta">
              {run.triggered_by ? <>Triggered by <strong>{run.triggered_by}</strong> · </> : null}
              {relativeTime(run.created_at)}
              {run.started_at && <> · ran {durationLabel(run.started_at, run.finished_at)}</>}
            </div>
          </div>
          <div className="cln-summary-actions">
            <span className={`cln-badge ${statusBadgeClass(run.status)}`}>{run.status}</span>
            {PRE_CONFIRM_STATUSES.has(run.status) && (
              <>
                <button
                  type="button"
                  className="cln-btn cln-btn--primary"
                  onClick={onResume}
                  disabled={cancelling}
                >
                  Continue
                </button>
                <button
                  type="button"
                  className="cln-btn cln-btn--ghost"
                  onClick={onCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel run'}
                </button>
              </>
            )}
            {run.status === 'completed' && (
              <a
                className="cln-btn cln-btn--ghost"
                href={`/api/cleanup/user-removal/runs/${run.id}/csv`}
                download
              >
                <DownloadIcon />
                CSV
              </a>
            )}
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh">
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run.error_message && <div className="cln-summary-error">{run.error_message}</div>}
      </section>

      <StageStrip run={run} items={items} />

      <section className="cln-summary-card">
        <div className="cln-summary-grid">
          <SummaryStat label="Pasted" value={run.emails_pasted} />
          <SummaryStat label="Requested" value={run.emails_requested} />
          <SummaryStat label="Targeted" value={run.emails_targeted} variant="ink" />
          <SummaryStat label="Not found" value={run.emails_not_found} variant="muted" />
          <SummaryStat label="Users deleted" value={run.users_deleted} variant="sage" />
          <SummaryStat label="Verified" value={run.users_verified} variant="sage" />
          <SummaryStat
            label="Failed"
            value={run.users_failed}
            variant={run.users_failed > 0 ? 'rose' : 'muted'}
          />
        </div>
      </section>

      <section className="cln-card">
        <div className="cln-card-header">
          <h2 className="cln-card-title">User results ({items.length})</h2>
        </div>

        {items.length === 0 && (
          <div className="cln-empty">
            <span>No items in this run yet.</span>
          </div>
        )}

        {items.length > 0 && (
          <table className="cln-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Domain</th>
                <th>Estate</th>
                <th>Panel</th>
                <th>Status</th>
                <th>Notes / error</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const isBad = it.status === 'error';
                return (
                  <tr key={it.id} className={isBad ? 'cln-row--bad' : undefined}>
                    <td className="cln-cell-mono">{it.user_email}</td>
                    <td className="cln-cell-mono cln-cell-faint">{it.domain_name || '-'}</td>
                    <td>
                      <span className="cln-badge cln-badge--muted">
                        {it.estate === 'microsoft' ? 'Microsoft' : 'Google'}
                      </span>
                    </td>
                    <td className="cln-cell-mono cln-cell-faint">
                      {it.estate === 'microsoft'
                        ? `MS tenant #${it.ms_tenant_id ?? '?'}`
                        : `${it.panel_reseller_id ?? '-'}${it.panel_email ? ` (${it.panel_email})` : ''}`}
                    </td>
                    <td>
                      <span className={`cln-badge ${itemBadgeClass(it.status)}`}>{it.status}</span>
                    </td>
                    <td className="cln-cell-faint">
                      {it.error_code && <span className="cln-error-tag">{it.error_code}</span>}
                      {it.error_message && <span className="cln-error-msg"> {it.error_message}</span>}
                      {it.status === 'error' && it.error_message && (
                        <div className="cln-cta-resolve">{it.error_message}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/* ─── Stage strip ────────────────────────────────────────── */

type StageState = 'pending' | 'running' | 'completed' | 'failed';

interface StageRow {
  key: 'a' | 'b1' | 'b2' | 'b3';
  name: string;
  state: StageState;
  progress?: { done: number; total: number };
  elapsed?: string;
}

function deriveStages(run: RemovalRun, items: RemovalRunItem[]): StageRow[] {
  const status = run.status;

  const stages: Record<StageRow['key'], StageState> = {
    a: 'pending',
    b1: 'pending',
    b2: 'pending',
    b3: 'pending',
  };

  if (status === 'phase_a_running' || status === 'queued') {
    stages.a = 'running';
  } else if (status === 'phase_a_done' || status === 'awaiting_confirmation') {
    stages.a = 'completed';
  } else if (status === 'phase_b1_running') {
    stages.a = 'completed';
    stages.b1 = 'running';
  } else if (status === 'phase_b2_waiting') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'running';
  } else if (status === 'phase_b3_running') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'running';
  } else if (status === 'completed') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'completed';
  } else if (status === 'failed') {
    if (run.error_message) {
      stages.a = 'failed';
    }
  }

  const total = Math.max(1, run.emails_targeted || items.length || 1);
  const b1Done = items.filter(
    (it) =>
      it.status === 'b1_user_deleted' ||
      it.status === 'b3_verified_deleted' ||
      it.status === 'error',
  ).length;
  const b3Done = items.filter(
    (it) => it.status === 'b3_verified_deleted' || it.status === 'error',
  ).length;

  const elapsedSinceStart = run.started_at
    ? durationLabel(run.started_at, run.finished_at)
    : '-';

  return [
    {
      key: 'a',
      name: 'Phase A',
      state: stages.a,
      elapsed: stages.a !== 'pending' ? elapsedSinceStart : undefined,
    },
    {
      key: 'b1',
      name: 'Phase B1',
      state: stages.b1,
      progress: stages.b1 === 'running' ? { done: b1Done, total } : undefined,
    },
    {
      key: 'b2',
      name: 'Phase B2',
      state: stages.b2,
    },
    {
      key: 'b3',
      name: 'Phase B3',
      state: stages.b3,
      progress: stages.b3 === 'running' ? { done: b3Done, total } : undefined,
    },
  ];
}

function StageStrip({
  run,
  items,
}: {
  run: RemovalRun;
  items: RemovalRunItem[];
}): JSX.Element {
  const stages = deriveStages(run, items);

  const labels: Record<StageRow['key'], string> = {
    a: 'Inventory pull',
    b1: 'Delete users',
    b2: 'Propagation wait',
    b3: 'Re-verify users',
  };

  return (
    <section className="cln-stage-strip">
      {stages.map((s) => {
        const pct =
          s.progress && s.progress.total > 0
            ? Math.min(100, Math.round((s.progress.done / s.progress.total) * 100))
            : 0;
        return (
          <div key={s.key} className="cln-stage-row">
            <span className="cln-stage-name">
              {s.name} - {labels[s.key]}
            </span>
            <div>
              {s.state === 'running' && s.progress ? (
                <div className="cln-stage-progress">
                  <div className="cln-stage-progress-bar" style={{ width: `${pct}%` }} />
                </div>
              ) : (
                <span style={{ display: 'inline-block', width: 200 }} />
              )}
            </div>
            <span className={`cln-stage-state cln-stage-state--${s.state}`}>
              {s.state === 'running' && s.progress
                ? `${s.progress.done}/${s.progress.total}`
                : s.state}
            </span>
            <span className="cln-stage-elapsed">{s.elapsed ?? ''}</span>
          </div>
        );
      })}
    </section>
  );
}

/* ─── SummaryStat (inline-copied from DomainRemoval) ─────── */

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted' | 'ink';
  hint?: string;
}): JSX.Element {
  return (
    <div className="cln-summary-stat">
      <span className="cln-summary-stat-label">{label}</span>
      <span className={`cln-summary-stat-value cln-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="cln-summary-stat-hint">{hint}</span>}
    </div>
  );
}
